import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Check, X, Repeat, AlertTriangle, Ban, Users } from 'lucide-react';
import { calendarService } from '../services/calendarService';
import { useLanguage } from '../i18n';
import { seriesSummary } from '../utils/calendarSeries';
import { CARD_ACTION_ROW, CARD_BTN_FIT, CARD_BTN_LABEL, CARD_ICON } from './cards/cardLayout';

// #533 — more than 5 different events in one message: ONE package card. A
// numbered list (date, time, title, invitees; a repeating event is ONE row with
// its series summary; needs-info / clash rows visibly marked), ONE Confirm and
// ONE Cancel for the whole package, and the explanation line under the buttons.
// The user may also answer in words ("all except 3") — the card then syncs from
// GET /api/calendar/packages/<id> (on mount and on every calendar refresh event).
// Rendered from the persisted message metadata, so it survives a reload.

const formatWhen = (item, locale, t) => {
    if (!item.start) return '';
    const d = new Date(item.start);
    if (Number.isNaN(d.getTime())) return '';
    const day = d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    if (item.tbc) return `${day} · ${t('caleTimeTbc', 'Time to be confirmed')}`;
    const hm = (x) => x.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    let text = `${day}, ${hm(d)}`;
    if (item.end) {
        const e = new Date(item.end);
        if (!Number.isNaN(e.getTime()) && e.toDateString() === d.toDateString()) text += `–${hm(e)}`;
    }
    return text;
};

const ROW_MARK = {
    needs_info: 'border-amber-400/40 bg-amber-400/[0.06]',
    clash: 'border-rose-400/40 bg-rose-400/[0.06]',
    error: 'border-rose-400/40 bg-rose-400/[0.06]',
    skipped: 'border-pai-hairline opacity-50',
    cancelled: 'border-pai-hairline opacity-50',
};

const statusOf = (pkg) => pkg?.resolution?.status || (pkg?.status && pkg.status !== 'pending' ? pkg.status : 'pending');

const CalendarPackageCard = ({ pkg, onResolved }) => {
    const { t, language } = useLanguage();
    const locale = language === 'es' ? 'es-ES' : 'en-GB';
    const [card, setCard] = useState(pkg || {});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // #545: the server can close this copy AFTER mount (a newer package card
    // replaced it — `resolution.superseded`); adopt a resolution that arrives in
    // the buffered payload while the card still shows pending.
    const incoming = pkg?.resolution;
    useEffect(() => {
        if (!incoming?.status) return;
        setCard((prev) => (statusOf(prev) === 'pending' ? { ...prev, resolution: incoming } : prev));
    }, [incoming]);
    const status = statusOf(card);
    const superseded = !!card.resolution?.superseded && status === card.resolution?.status;
    const isPending = status === 'pending';
    const items = Array.isArray(card.items) ? card.items : [];
    const packageId = card.package_id;

    const adopt = useCallback((fresh) => {
        if (!fresh || typeof fresh !== 'object') return;
        setCard((prev) => {
            const next = { ...prev, ...fresh };
            // Keep the buffered message in step so switching chats repaints the
            // final state immediately (the server already stamped the metadata).
            if (pkg && typeof pkg === 'object') {
                pkg.items = next.items;
                if (next.resolution) pkg.resolution = next.resolution;
            }
            return next;
        });
    }, [pkg]);

    // Sync from the server while the package is open: it may have been answered
    // in words, on Telegram, or edited by number.
    useEffect(() => {
        if (!packageId || !isPending) return undefined;
        let alive = true;
        const refresh = () => {
            calendarService.getPackage(packageId)
                .then((data) => { if (alive) adopt(data); })
                .catch(() => { /* keep the persisted copy */ });
        };
        refresh();
        window.addEventListener('omnis:calendar-event-updated', refresh);
        return () => {
            alive = false;
            window.removeEventListener('omnis:calendar-event-updated', refresh);
        };
    }, [packageId, isPending, adopt]);

    const resolve = async (action) => {
        if (!packageId || saving) return;
        setSaving(true);
        setError(null);
        try {
            const res = await calendarService.resolvePackage(packageId, action);
            adopt(res?.card || { resolution: res?.resolution, status: res?.status });
            try {
                window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated', { detail: { package_id: packageId } }));
            } catch (_) { /* no-op */ }
            onResolved?.(res);
        } catch (err) {
            setError(t('calePkgError409', 'This package was already handled.'));
            calendarService.getPackage(packageId).then(adopt).catch(() => {});
        } finally {
            setSaving(false);
        }
    };

    const badge = {
        pending: { cls: 'bg-amber-400/10 text-amber-300', label: t('calePkgPending', 'Pending') },
        confirmed: { cls: 'bg-green-400/10 text-green-300', label: t('calePkgConfirmed', 'Confirmed') },
        cancelled: { cls: 'bg-white/[0.06] text-pai-meta', label: t('calePkgCancelled', 'Cancelled') },
    }[status] || { cls: 'bg-white/[0.06] text-pai-meta', label: status };
    if (superseded) badge.label = t('cardSuperseded', 'Replaced by the updated card');

    const created = card.resolution?.count ?? items.filter((i) => i.status === 'created').length;
    // PO 2026-11-01: an edit re-shows the package as a NEW card ("Updated list");
    // the header counts the lines that would still be created.
    const activeCount = items.filter((i) => !['skipped', 'cancelled'].includes(i.status)).length;
    const isUpdated = Boolean(card.updated);

    return (
        <div
            data-testid="calendar-package-card"
            data-status={status}
            data-count={items.length}
            data-active-count={activeCount}
            data-updated={isUpdated ? 'true' : undefined}
            data-superseded={superseded ? 'true' : undefined}
            className="relative p-3 min-w-0 max-w-full bg-white/[0.03] border border-pai rounded-lg text-xs"
        >
            <div className="flex items-center gap-2 mb-2 min-w-0">
                <Calendar size={14} className="text-amber-400 shrink-0" />
                <span className="font-medium text-white truncate">{t('calePkgTitle', 'Calendar events')}</span>
                <span className="text-pai-meta shrink-0">{t('calePkgCount', '{n} events', { n: activeCount })}</span>
                {isUpdated && (
                    <span data-testid="calendar-package-updated" className="px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 bg-pai-blue/10 text-pai-blue">
                        {t('calePkgUpdated', 'Updated list')}
                    </span>
                )}
                <span data-testid="calendar-package-status" className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${badge.cls}`}>
                    {badge.label}
                </span>
            </div>

            <ol data-testid="calendar-package-list" className="space-y-1 max-h-80 overflow-y-auto pr-1">
                {items.map((item) => {
                    const series = item.series && item.series.count ? item.series : null;
                    const when = series ? seriesSummary(series, t, language) : formatWhen(item, locale, t);
                    const firstClash = (item.conflicts || [])[0];
                    return (
                        <li
                            key={item.n}
                            data-testid="calendar-package-row"
                            data-n={item.n}
                            data-status={item.status}
                            className={`flex gap-2 min-w-0 px-2 py-1 rounded border ${ROW_MARK[item.status] || 'border-transparent'}`}
                        >
                            <span className="text-pai-meta tabular-nums shrink-0 w-6 text-right">{item.n}.</span>
                            <div className="min-w-0 flex-1">
                                <div className={`text-white break-words ${['skipped', 'cancelled'].includes(item.status) ? 'line-through' : ''}`}>
                                    {item.title}
                                </div>
                                {when && (
                                    <div className="flex items-start gap-1 text-pai-meta">
                                        {series && <Repeat size={11} className="mt-0.5 shrink-0" />}
                                        <span data-testid={series ? 'calendar-package-series' : undefined} className="break-words">{when}</span>
                                    </div>
                                )}
                                {item.invitees?.length > 0 && (
                                    <div className="flex items-start gap-1 text-pai-meta">
                                        <Users size={11} className="mt-0.5 shrink-0" />
                                        <span className="break-words">{t('calePkgWith', 'with {names}', { names: item.invitees.join(', ') })}</span>
                                    </div>
                                )}
                                {/* #536: a name that is no network contact / team — shown, never invited. */}
                                {item.not_invited?.length > 0 && !['skipped', 'cancelled'].includes(item.status) && (
                                    <div data-testid="calendar-package-not-invited" className="flex items-start gap-1 text-amber-300">
                                        <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                                        <span className="break-words">{t('calePkgNotInvited', 'Not invited: {names} (not in your network)', { names: item.not_invited.map((x) => x.name).join(', ') })}</span>
                                    </div>
                                )}
                                {item.status === 'needs_info' && (
                                    <div data-testid="calendar-package-needs-info" className="flex items-center gap-1 text-amber-300">
                                        <AlertTriangle size={11} className="shrink-0" />
                                        <span>{t('calePkgNeedsInfo', 'Needs info: {what}', { what: item.missing || '' })}</span>
                                    </div>
                                )}
                                {item.status === 'clash' && (
                                    <div data-testid="calendar-package-clash" className="flex items-center gap-1 text-rose-300">
                                        <Ban size={11} className="shrink-0" />
                                        <span className="break-words">{t('calePkgClash', 'Clashes with {title}', { title: firstClash?.title || '' })}</span>
                                    </div>
                                )}
                                {item.status === 'error' && (
                                    <div className="flex items-center gap-1 text-rose-300">
                                        <Ban size={11} className="shrink-0" />
                                        <span className="break-words">{t('calePkgError', 'Can\'t be created: {reason}', { reason: item.reason || '' })}</span>
                                    </div>
                                )}
                            </div>
                            {item.status === 'created' && (
                                <Check size={12} aria-label={t('calePkgCreated', 'Created')} className="text-green-400 shrink-0 mt-0.5" />
                            )}
                            {item.status === 'skipped' && (
                                <span className="text-pai-meta shrink-0">{t('calePkgSkipped', 'Skipped')}</span>
                            )}
                            {item.changed && item.status !== 'skipped' && (
                                <span className="text-pai-blue shrink-0">{t('calePkgChanged', 'Changed')}</span>
                            )}
                        </li>
                    );
                })}
            </ol>

            {isPending ? (
                <>
                    <div data-testid="card-action-row" className={CARD_ACTION_ROW.replace('mt-3', 'mt-2')}>
                        <button
                            data-testid="calendar-package-confirm"
                            onClick={() => resolve('confirm')}
                            disabled={saving}
                            className={`flex items-center gap-1 px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium disabled:opacity-50 transition-colors ${CARD_BTN_FIT}`}
                        >
                            <Check size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('commonConfirm', 'Confirm')}</span>
                        </button>
                        <button
                            data-testid="calendar-package-cancel"
                            onClick={() => resolve('cancel')}
                            disabled={saving}
                            className={`flex items-center gap-1 px-3 py-1 border border-red-300 dark:border-red-700 rounded text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors ${CARD_BTN_FIT}`}
                        >
                            <X size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('commonCancel', 'Cancel')}</span>
                        </button>
                    </div>
                    {isUpdated && (
                        <p data-testid="calendar-package-updated-note" className="mt-2 text-pai-secondary break-words">
                            {t('calePkgUpdatedNote', 'Nothing has been created yet.')}
                        </p>
                    )}
                    <p data-testid="calendar-package-explain" className="mt-2 text-pai-secondary break-words">
                        {t('calePkgExplain', 'Press Confirm to create them all, or tell me which numbers to change or remove and I\'ll show you the updated list.')}
                    </p>
                </>
            ) : (
                <div data-testid="calendar-package-resolution" data-status={status} className="mt-2 text-pai-secondary break-words">
                    {status === 'cancelled'
                        ? t('calePkgResolvedCancelled', 'Cancelled — none of these events were created.')
                        : t('calePkgResolvedConfirmed', 'Created {created} of {total} events.', { created, total: items.length })}
                    {status !== 'cancelled' && card.resolution?.invited?.length > 0 && (
                        // #536: the same Confirm sent ONE invitation per person.
                        <div data-testid="calendar-package-invited" className="mt-1">
                            {t('calePkgInvited', 'Invitations sent: {people}.', {
                                people: card.resolution.invited
                                    .map((p) => `${p.name} (${p.events === 1 ? t('calePkgOneEvent', '1 event') : t('calePkgCount', '{n} events', { n: p.events })})`)
                                    .join(', '),
                            })}
                        </div>
                    )}
                </div>
            )}
            {error && <div className="mt-1 text-[11px] text-rose-400">{error}</div>}
        </div>
    );
};

export default CalendarPackageCard;
